import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { companiesService } from '@/features/companies/services/companies.service';
import { CompanyDetail } from '@/features/companies/components/CompanyDetail';
import { CompanyForm } from '@/features/companies/components/CompanyForm';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export const Route = createFileRoute('/_authenticated/companies/$id')({
  component: CompanyDetailsPage,
});

function CompanyDetailsPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const { data: company, isLoading } = useQuery({
    queryKey: ['company', id],
    queryFn: () => companiesService.getCompany(id),
  });

  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof companiesService.updateCompany>[1]) =>
      companiesService.updateCompany(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', id] });
      queryClient.invalidateQueries({ queryKey: ['companies-list'] });
      toast({ title: 'Empresa atualizada com sucesso' });
      setEditDialogOpen(false);
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" aria-label="Voltar" onClick={() => navigate({ to: '/companies' })}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">{company?.name || 'Empresa'}</h1>
          <p className="text-muted-foreground">Detalhes da empresa</p>
        </div>
      </div>

      <CompanyDetail
        company={company}
        isLoading={isLoading}
        onEdit={() => setEditDialogOpen(true)}
      />

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent size="lg" busy={updateMutation.isPending}>
          <DialogHeader>
            <DialogTitle>Editar Empresa</DialogTitle>
          </DialogHeader>
          <CompanyForm
            initialData={company}
            onSubmit={(data) => updateMutation.mutate(data)}
            onCancel={() => setEditDialogOpen(false)}
            isLoading={updateMutation.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
